import React, { useCallback, useEffect, useMemo, useState } from 'react';
import TextField, { type TextFieldProps } from '@mui/material/TextField';
import type { FormHelperTextProps } from '@mui/material/FormHelperText';

export interface NumericTextFieldProps
  extends Omit<TextFieldProps, 'value' | 'onChange' | 'onBlur' | 'type'> {
  value: number | null;
  onChange: (value: number | null) => void;
  min?: number;
  max?: number;
  precision?: number;
  onBlur?: (value: number | null) => void;
}

const NumericTextField: React.FC<NumericTextFieldProps> = ({
  value,
  onChange,
  onBlur,
  min,
  max,
  precision,
  helperText,
  error,
  slotProps,
  onFocus,
  sx,
  ...rest
}) => {
  const formatValue = useCallback(
    (val: number | null) => {
      if (val === null || val === undefined) return '';
      return precision !== undefined ? val.toFixed(precision) : String(val);
    },
    [precision],
  );

  const [inputValue, setInputValue] = useState<string>(() => formatValue(value));
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (!isFocused) {
      setInputValue(formatValue(value));
    }
  }, [value, formatValue, isFocused]);

  const allowNegative = min !== undefined && min < 0;

  const outOfRange =
    value !== null &&
    ((min !== undefined && value < min) ||
      (max !== undefined && value > max));

  const finalHelperText = outOfRange
    ? `Range: ${min ?? 0} – ${max ?? '+∞'}`
    : helperText;

  const numericPattern = useMemo(() => {
    return allowNegative ? /^-?\d*\.?\d*$/ : /^\d*\.?\d*$/;
  }, [allowNegative]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;

      if (!numericPattern.test(raw)) {
        return;
      }

      setInputValue(raw);

      if (raw.trim() === '') {
        onChange(null);
        return;
      }

      if (raw === '.' || raw === '-' || raw === '-.') {
        return;
      }

      const parsed = Number(raw);

      if (!Number.isNaN(parsed)) {
        onChange(parsed);
      }
    },
    [numericPattern, onChange],
  );

  const handleFocus = useCallback(
    (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setIsFocused(true);
      onFocus?.(e);
    },
    [onFocus],
  );

  const handleBlur = useCallback(() => {
    setIsFocused(false);

    const raw = inputValue.trim();

    if (raw === '' || raw === '.' || raw === '-' || raw === '-.') {
      setInputValue('');
      onChange(null);
      onBlur?.(null);
      return;
    }

    const parsed = Number(raw);

    if (Number.isNaN(parsed)) {
      setInputValue('');
      onChange(null);
      onBlur?.(null);
      return;
    }

    const finalValue =
      precision !== undefined ? Number(parsed.toFixed(precision)) : parsed;

    setInputValue(formatValue(finalValue));
    onChange(finalValue);
    onBlur?.(finalValue);
  }, [formatValue, inputValue, onBlur, onChange, precision]);

  const formHelperTextSlotProps =
    typeof slotProps?.formHelperText === 'function'
      ? undefined
      : (slotProps?.formHelperText as FormHelperTextProps | undefined);

  const htmlInputSlotProps =
    typeof slotProps?.htmlInput === 'function'
      ? undefined
      : slotProps?.htmlInput;

  return (
    <TextField
      {...rest}
      sx={{ containerType: 'inline-size', ...sx }}
      type="text"
      value={inputValue}
      onFocus={handleFocus}
      onChange={handleChange}
      onBlur={handleBlur}
      error={Boolean(error || outOfRange)}
      helperText={finalHelperText}
      slotProps={{
        ...slotProps,
        htmlInput: {
          ...htmlInputSlotProps,
          inputMode: 'decimal',
          ...(min !== undefined ? { min } : {}),
          ...(max !== undefined ? { max } : {}),
        },
        formHelperText: {
          ...formHelperTextSlotProps,
          sx: {
            fontSize: 'clamp(0.7rem, 5.5cqi, 0.9rem)',
            whiteSpace: 'nowrap',
            minHeight: '1.2em',
            margin: 0.3,
            ...formHelperTextSlotProps?.sx,
          },
        },
      }}
    />
  );
};

export default React.memo(NumericTextField);